import type { AppointmentData, AdminScheduleSlot } from '../api/appointmentApi';

export interface ScheduleRow {
  time: string;
  isAvailable: boolean;
  intervalMinutes?: number;
  date?: string;
  appointmentId?: string;
  patientName?: string;
  phoneNumber?: string;
  idCard?: string;
  provider?: string;
  isSynced?: boolean;
  clinicName?: string;
  createdBy?: string;
  createdAt?: string;
  serviceName?: string;
  serviceId?: string;
  village?: string;
  isReserveSlot?: boolean;
  status?: string;
  confirmationStatus?: string;
  confirmationToken?: string;
  reminderSent?: boolean;
  capacity?: number;
  booked?: number;
  technicianId?: string;
  technicianName?: string;
  appointments?: AdminScheduleSlot['appointments'];
}

export type SavedAppointment = AppointmentData & {
  confirmationToken?: string;
  technicianId?: string;
  technicianName?: string;
};

function recount(rows: ScheduleRow[], time: string): ScheduleRow[] {
  const appointments = rows.filter(row => row.time === time && row.appointmentId && row.status !== 'cancelled').map(row => ({
    appointmentId: row.appointmentId!, patientName: row.patientName || '', phoneNumber: row.phoneNumber || '',
    idCard: row.idCard, provider: row.provider || '', serviceName: row.serviceName || '', serviceId: row.serviceId,
    isSynced: Boolean(row.isSynced), status: row.status || 'completed', confirmationStatus: row.confirmationStatus || '',
    confirmationToken: row.confirmationToken, reminderSent: Boolean(row.reminderSent), isReserveSlot: Boolean(row.isReserveSlot),
    createdBy: row.createdBy || '', clinicName: row.clinicName || '', village: row.village || '', createdAt: row.createdAt || '',
  }));
  return rows.map(row => row.time === time ? { ...row, booked: appointments.length, appointments } : row);
}

/** Remove only this appointment; keep the regular time and every other booking. */
export function releaseAppointment(rows: ScheduleRow[], appointmentId: string): ScheduleRow[] {
  const previous = rows.find(row => row.appointmentId === appointmentId);
  if (!previous) return rows;
  let remaining = rows.filter(row => row.appointmentId !== appointmentId);
  if (!remaining.some(row => row.time === previous.time) && !previous.isReserveSlot) {
    remaining = [...remaining, {
      time: previous.time, date: previous.date, isAvailable: true,
      intervalMinutes: previous.intervalMinutes, capacity: previous.capacity, booked: 0,
      technicianId: previous.technicianId, technicianName: previous.technicianName, appointments: [],
    }];
  }
  return recount(remaining, previous.time).sort((a, b) => a.time.localeCompare(b.time));
}

/** Use the full saved record, including fields needed by Edit and Move. No GET is needed. */
export function applySavedAppointment(rows: ScheduleRow[], saved: SavedAppointment, template: ScheduleRow): ScheduleRow[] {
  const previous = rows.find(row => row.appointmentId === saved.appointmentId);
  let remaining = previous ? releaseAppointment(rows, previous.appointmentId!) : rows;
  const destination = remaining.find(row => row.time === saved.selectedTime && row.isAvailable)
    || remaining.find(row => row.time === saved.selectedTime);
  const base = destination || template;
  const appointment: ScheduleRow = {
    ...base, time: saved.selectedTime, date: saved.selectedDay, isAvailable: false,
    appointmentId: saved.appointmentId, patientName: saved.fullName, phoneNumber: saved.phoneNumber,
    idCard: saved.idCard, village: saved.village, provider: saved.provider,
    serviceId: saved.serviceId, serviceName: saved.serviceName, status: saved.status,
    clinicName: saved.clinicName, createdBy: saved.createdBy, createdAt: saved.createdAt ?? previous?.createdAt,
    isSynced: saved.isSynced ?? previous?.isSynced ?? false, isReserveSlot: Boolean(saved.isReserveSlot),
    confirmationStatus: saved.confirmationStatus ?? previous?.confirmationStatus,
    confirmationToken: saved.confirmationToken ?? previous?.confirmationToken,
    reminderSent: saved.reminderSent ?? previous?.reminderSent ?? false,
    technicianId: saved.technicianId ?? base.technicianId, technicianName: saved.technicianName ?? base.technicianName,
    intervalMinutes: saved.intervalMinutes ?? base.intervalMinutes,
  };
  remaining = remaining.filter(row => row.time !== saved.selectedTime || !row.isAvailable || Boolean(row.appointmentId));
  return recount([...remaining, appointment], saved.selectedTime).sort((a, b) => a.time.localeCompare(b.time));
}
